/**
 * chunkedUploadService.js
 *
 * Uploads a single File to S3 via the backend multipart endpoints.
 *
 * Features:
 *  - 10 MB chunks
 *  - 3 concurrent chunk uploads (parallel window for speed)
 *  - 3 retries per chunk on transient network errors
 *  - localStorage resume: re-selecting the same file resumes from the last
 *    completed part without re-uploading already-finished chunks
 *  - onProgress(percent 0–100) callback fires after each chunk lands
 *  - Calls /uploads/multipart/abort on unrecoverable error to avoid
 *    leaving orphaned parts in S3
 */

import { uploadApi } from '../api';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB
const CONCURRENCY = 3;               // parallel part uploads
const MAX_RETRIES = 3;               // per-chunk retry attempts
const MAX_FILE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB hard limit

// ---------------------------------------------------------------------------
// localStorage resume helpers
// ---------------------------------------------------------------------------

function resumeKey(file) {
  return `mp_resume_${encodeURIComponent(file.name)}_${file.size}_${file.lastModified}`;
}

function loadResume(file) {
  try {
    const raw = localStorage.getItem(resumeKey(file));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveResume(file, state) {
  try {
    localStorage.setItem(resumeKey(file), JSON.stringify(state));
  } catch {
    // quota exceeded or private mode — ignore
  }
}

function clearResume(file) {
  try {
    localStorage.removeItem(resumeKey(file));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Single chunk upload with retry
// ---------------------------------------------------------------------------

async function uploadPartWithRetry(s3Key, uploadId, partNumber, blob, attempt = 1) {
  const form = new FormData();
  form.append('uploadId', uploadId);
  form.append('s3Key', s3Key);
  form.append('partNumber', String(partNumber));
  form.append('chunk', blob, 'chunk');

  try {
    const res = await uploadApi.post('/uploads/multipart/part', form);
    return res.data; // { etag, partNumber }
  } catch (err) {
    if (attempt >= MAX_RETRIES) throw err;
    // exponential back-off: 500ms, 1s, 2s …
    await new Promise(r => setTimeout(r, 500 * 2 ** (attempt - 1)));
    return uploadPartWithRetry(s3Key, uploadId, partNumber, blob, attempt + 1);
  }
}

// ---------------------------------------------------------------------------
// Parallel upload window
// ---------------------------------------------------------------------------

/**
 * Runs `tasks` (array of async-factory functions) with at most `concurrency`
 * running at the same time. Returns results in task order.
 */
async function parallelLimit(tasks, concurrency) {
  const results = new Array(tasks.length);
  let index = 0;

  async function worker() {
    while (index < tasks.length) {
      const i = index++;
      results[i] = await tasks[i](); // eslint-disable-line no-await-in-loop
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, worker);
  await Promise.all(workers);
  return results;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Upload a File using S3 multipart upload.
 *
 * @param {File} file               The File object to upload.
 * @param {object} options
 * @param {string} options.context  'campaign_file' | 'creative_deliverable'
 * @param {string} [options.campaignId]   Required for campaign_file
 * @param {string} [options.label]        Required for campaign_file
 * @param {string} [options.assignmentId] Required for creative_deliverable
 * @param {function} [options.onProgress] Called with (percent: 0–100) after each part
 * @returns {Promise<{url: string, item: object}>}
 */
export async function uploadFileInChunks(file, options = {}) {
  const { context, campaignId, label, assignmentId, onProgress } = options;

  // ---- 1. Client-side size guard ----
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`File "${file.name}" exceeds the 5 GB upload limit.`);
  }

  const totalParts = Math.ceil(file.size / CHUNK_SIZE) || 1;

  // ---- 2. Check for resume state ----
  let resumeState = loadResume(file);
  let uploadId, s3Key;
  let completedParts = {}; // { [partNumber]: etag }

  if (
    resumeState &&
    resumeState.uploadId &&
    resumeState.s3Key
  ) {
    uploadId = resumeState.uploadId;
    s3Key = resumeState.s3Key;
    completedParts = resumeState.completedParts || {};
  } else {
    // ---- 3. Initiate new multipart upload ----
    const initRes = await uploadApi.post('/uploads/multipart/initiate', {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      context,
      campaignId,
      label,
      assignmentId,
    });
    uploadId = initRes.data.uploadId;
    s3Key = initRes.data.s3Key;
    completedParts = {};
    saveResume(file, { uploadId, s3Key, completedParts, totalParts });
  }

  // ---- 4. Build list of pending parts ----
  const pendingPartNumbers = [];
  for (let i = 1; i <= totalParts; i++) {
    if (!completedParts[i]) pendingPartNumbers.push(i);
  }

  const uploadedSoFar = Object.keys(completedParts).length;
  let doneCount = uploadedSoFar;

  // ---- 5. Upload pending parts in parallel batches ----
  const tasks = pendingPartNumbers.map(partNumber => async () => {
    const start = (partNumber - 1) * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    let result;
    try {
      result = await uploadPartWithRetry(s3Key, uploadId, partNumber, blob);
    } catch (err) {
      // Unrecoverable — abort S3 multipart to clean up
      try { await uploadApi.post('/uploads/multipart/abort', { uploadId, s3Key }); } catch {}
      clearResume(file);
      throw new Error(`Failed to upload part ${partNumber} of "${file.name}": ${err.message}`);
    }

    completedParts[partNumber] = result.etag;
    doneCount += 1;

    // Save resume progress
    saveResume(file, { uploadId, s3Key, completedParts, totalParts });

    if (typeof onProgress === 'function') {
      onProgress(Math.round((doneCount / totalParts) * 100));
    }

    return result;
  });

  await parallelLimit(tasks, CONCURRENCY);

  // ---- 6. Complete multipart upload ----
  const parts = Object.entries(completedParts).map(([pn, etag]) => ({
    partNumber: Number(pn),
    etag,
  }));

  let completeRes;
  try {
    completeRes = await uploadApi.post('/uploads/multipart/complete', {
      uploadId,
      s3Key,
      parts,
      context,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      campaignId,
      label,
      assignmentId,
    });
  } catch (err) {
    // Don't abort here — parts are complete, just the final step failed.
    // Leave resume state so the user can retry the complete step.
    throw new Error(`Failed to finalise upload for "${file.name}": ${err.message}`);
  }

  clearResume(file);

  if (typeof onProgress === 'function') onProgress(100);

  return completeRes.data; // { url, item }
}

export default { uploadFileInChunks };
